import { object, string } from 'yup';

import {
  getConditionalObject,
  replaceEmptyWithNull,
  replaceFalsyValuesWithInitialValues,
} from 'utils/data';
import { checkBooleanPendingChangeVisibility, mergePendingChanges } from 'utils/pendingChanges';

import { ALL_BORROWERS_PATH, NEW_RECORD_DATA } from '../../../../FinancialInfo.constants';
import { mapFieldsToFalse } from '../SectionContent.utils';

import { getConfigFlags } from './Assets.config';
import { GiftFundsProvisionPeriodType } from './Assets.constants';

const formattedKeys = {
  institution: 'financialInstitution',
  amount: 'cashOrMarketValue',
  balance: 'cashOrMarketValue',
  otherSourceDescription: 'otherSourceOfGrantDescription',
};

const booleanFields = ['isDeposited', 'includeInAssets', 'isUsedForDownPayment', 'isUsedForIncome'];

const defaultBooleanValues = mapFieldsToFalse(booleanFields);

export const normalizeAssetsReceivedData = data =>
  Object.entries(data).reduce(
    (
      acc,
      [
        id,
        {
          ownerIds: owners,
          isUsedForDownPayment = false,
          isUsedForIncome = false,
          type: assetData,
          isVerified,
        },
      ],
    ) => {
      const rawAssetData = {};

      const formattedAssetData = Object.entries(assetData).reduce(
        (assetDataAcc, [key, value]) => {
          const formattedKey = formattedKeys[key] ?? key;

          switch (key) {
            case 'fundsStatus':
              {
                const { type, fundsWillBeProvidedAtClosing } = value;

                assetDataAcc.isDeposited = type === 'Deposited';
                assetDataAcc.giftFundsProvisionPeriod = fundsWillBeProvidedAtClosing
                  ? GiftFundsProvisionPeriodType.AT_CLOSING
                  : GiftFundsProvisionPeriodType.PRIOR_TO_CLOSING;
                rawAssetData.isDeposited = type;
                rawAssetData.giftFundsProvisionPeriod = fundsWillBeProvidedAtClosing;
              }

              break;

            case 'includeInAssets':
              assetDataAcc[formattedKey] = value || false;
              rawAssetData[formattedKey] = value;
              break;

            default:
              assetDataAcc[formattedKey] = value;
              rawAssetData[formattedKey] = value;
          }

          return assetDataAcc;
        },
        {
          giftFundsProvisionPeriod: GiftFundsProvisionPeriodType.PRIOR_TO_CLOSING,
          ...defaultBooleanValues,
        },
      );

      acc.formattedData[id] = {
        ...formattedAssetData,
        owners,
        isUsedForDownPayment,
        isUsedForIncome,
        isVerified,
      };
      acc.rawData[id] = {
        ...rawAssetData,
        owners,
        isUsedForDownPayment,
        isUsedForIncome,
        isVerified,
      };

      return acc;
    },
    {
      formattedData: {},
      rawData: {},
    },
  );

export const normalizeAssetsDataToSend = ({ deletedRecords, rawData, dataToNormalize }) => {
  const { normalizedDataToUpdate, normalizedDataToCreate } = Object.entries(dataToNormalize).reduce(
    (acc, [recordId, recordData]) => {
      if (deletedRecords.has(recordId)) {
        return acc;
      }

      const {
        owners,
        type,
        financialInstitution,
        accountNumber,
        cashOrMarketValue,
        gifterName,
        sourceOfFunds,
        relationship,
        closingCosts,
        isDeposited,
        giftFundsProvisionPeriod,
        sourceType,
        otherSourceOfGrantDescription,
        grantSourceName,
        downPayment,
        isUsedForDownPayment,
        isUsedForIncome,
        includeInAssets,
      } = recordData;

      const {
        isGrantType,
        isGiftOfPropertyEquity,
        isAssetTypeGift,
        isMainAssetType,
        isOtherAssetType,
        isSourceTypeOther,
      } = getConfigFlags({ type: type?.value, sourceType: sourceType?.value });

      const { isVerified = false, owners: initialOwners } = rawData[recordId] ?? {};
      const ownersToSend = owners ? owners.map(({ value }) => value) : initialOwners;

      const normalizedRecordData = {
        owners: ownersToSend,
        ...getConditionalObject(!isVerified, () => ({
          type: type?.value,
          ...getConditionalObject(isMainAssetType, () => ({
            ...replaceEmptyWithNull({
              financialInstitution,
              accountNumber,
              cashOrMarketValue,
            }),
          })),
          ...getConditionalObject('closingCosts' in recordData, () => ({
            ...replaceEmptyWithNull({
              closingCosts,
            }),
          })),
          ...getConditionalObject(isAssetTypeGift || isGiftOfPropertyEquity, () => ({
            gifterName,
            cashOrMarketValue,
            isDeposited,
            ...getConditionalObject(isAssetTypeGift, () => ({
              ...getConditionalObject('downPayment' in recordData, () => ({
                ...replaceEmptyWithNull({
                  downPayment,
                }),
              })),
            })),
            ...(isGiftOfPropertyEquity
              ? { sourceOfFunds: sourceOfFunds?.value, sourceType: sourceOfFunds?.value }
              : { relationship: relationship?.value, sourceType: relationship?.value }),
            ...getConditionalObject(giftFundsProvisionPeriod, {
              giftFundsProvisionPeriod,
            }),
          })),
          ...getConditionalObject(isGrantType, () => ({
            grantSourceName,
            ...getConditionalObject(sourceType !== undefined, () => ({
              sourceType: sourceType?.value,
              ...getConditionalObject(isSourceTypeOther, { otherSourceOfGrantDescription }),
            })),
            ...getConditionalObject('downPayment' in recordData, () => ({
              ...replaceEmptyWithNull({
                downPayment,
              }),
            })),
          })),
          ...getConditionalObject(isUsedForDownPayment !== undefined, {
            isUsedForDownPayment,
          }),
          ...getConditionalObject(isUsedForIncome !== undefined, {
            isUsedForIncome,
          }),
          ...getConditionalObject(isOtherAssetType, {
            cashOrMarketValue,
          }),
          ...getConditionalObject(isGrantType || isAssetTypeGift, () => ({
            ...replaceFalsyValuesWithInitialValues({ includeInAssets }, rawData[recordId]),
          })),
        })),
      };

      if (recordId.startsWith(NEW_RECORD_DATA.id)) {
        acc.normalizedDataToCreate.push(normalizedRecordData);
      }

      if (!recordId.startsWith(NEW_RECORD_DATA.id)) {
        acc.normalizedDataToUpdate[recordId] = {
          type: isVerified ? 'Validated' : 'Full',
          updated: normalizedRecordData,
        };
      }

      return acc;
    },
    { normalizedDataToUpdate: {}, normalizedDataToCreate: [] },
  );

  return {
    create: normalizedDataToCreate,
    update: normalizedDataToUpdate,
    delete: Array.from(deletedRecords),
  };
};

export const normalizeForm = ({
  form,
  recordId,
  isPurchaseFlow,
  isRefinanceFlow,
  isAssetTypeSourceOfGrantEnabled,
  borrowerId,
  ownersOptions,
}) => {
  const {
    owners,
    type,
    financialInstitution,
    accountNumber,
    cashOrMarketValue,
    gifterName,
    sourceOfFunds,
    relationship,
    closingCosts,
    sourceType,
    grantSourceName,
    otherSourceOfGrantDescription,
    isDeposited,
    downPayment,
    includeInAssets,
    giftFundsProvisionPeriod,
    isUsedForDownPayment,
    isUsedForIncome,
  } = form[recordId] ?? {};

  const {
    isGrantType,
    isGiftOfPropertyEquity,
    isAssetTypeGift,
    isMainAssetType,
    isOtherAssetType,
    isSourceTypeOther,
  } = getConfigFlags({ type: type?.value, sourceType: sourceType?.value });

  const initialOwners =
    recordId.startsWith(NEW_RECORD_DATA.id) &&
    ownersOptions.length &&
    borrowerId !== ALL_BORROWERS_PATH
      ? [ownersOptions.find(({ value }) => value === borrowerId)]
      : owners;

  return {
    ...form,
    [recordId]: {
      owners: owners ?? initialOwners,
      type,
      ...getConditionalObject(isMainAssetType, {
        financialInstitution,
        accountNumber,
        cashOrMarketValue,
      }),
      ...getConditionalObject(isAssetTypeGift || isGiftOfPropertyEquity, () => ({
        gifterName,
        ...(isGiftOfPropertyEquity
          ? { sourceOfFunds: sourceOfFunds ?? sourceType }
          : { relationship: relationship ?? sourceType }),
        cashOrMarketValue,
        ...getConditionalObject(isAssetTypeGift && (isPurchaseFlow || isRefinanceFlow), () => ({
          closingCosts,
          ...getConditionalObject(isPurchaseFlow, {
            downPayment,
          }),
        })),
        isDeposited,
        giftFundsProvisionPeriod: !isDeposited
          ? giftFundsProvisionPeriod
          : GiftFundsProvisionPeriodType.PRIOR_TO_CLOSING,
      })),
      ...getConditionalObject(isGrantType, () => ({
        ...getConditionalObject(isAssetTypeSourceOfGrantEnabled, () => ({
          sourceType,
          ...getConditionalObject(isSourceTypeOther, { otherSourceOfGrantDescription }),
        })),
        ...getConditionalObject(isPurchaseFlow || isRefinanceFlow, {
          closingCosts,
        }),
        ...getConditionalObject(isPurchaseFlow, {
          downPayment,
        }),
        grantSourceName,
      })),
      ...getConditionalObject(isPurchaseFlow && !isGrantType && !isAssetTypeGift, {
        isUsedForDownPayment,
      }),
      isUsedForIncome,
      ...getConditionalObject(isOtherAssetType, {
        cashOrMarketValue,
      }),
      ...getConditionalObject(isGrantType || isAssetTypeGift, {
        includeInAssets,
      }),
    },
  };
};

export const getCustomAssetsSchema = ({ schema, isCustomValidation, isPurchaseFlow, recordId }) => {
  if (!isCustomValidation) {
    return schema;
  }

  return schema.concat(
    object().shape({
      [recordId]: object().shape({
        cashOrMarketValue: string().test(
          'is-greater-than-sum',
          isPurchaseFlow
            ? 'Cannot be less than the sum of Used For Closing Costs and Down Payment.'
            : 'Cannot be less than Used For Closing Costs.',
          function (value) {
            const { closingCosts = null, downPayment = null } = this.parent ?? {};

            return isPurchaseFlow
              ? Number(value) >= Number(closingCosts) + Number(downPayment)
              : Number(value) >= Number(closingCosts);
          },
        ),
      }),
    }),
  );
};

const filterPendingChanges = ({ pendingChanges, isAssetTypeSourceOfGrantEnabled }) =>
  Object.entries(pendingChanges).reduce((acc, [recordId, recordPendingChanges]) => {
    const filteredPendingChanges = Object.entries(recordPendingChanges).reduce(
      (filteredPendingChangesAcc, [pendingChangeKey, pendingChangeValue]) => {
        const assetTypePendingChangeNewValue = recordPendingChanges.type?.newValue;

        const isAssetTypeChanged = assetTypePendingChangeNewValue !== undefined;

        const {
          isGrantType,
          isGiftOfPropertyEquity,
          isAssetTypeGift,
          isMainAssetType,
          isOtherAssetType,
          isSourceTypeOther,
        } = getConfigFlags({ type: assetTypePendingChangeNewValue });

        const getCondition = () => {
          switch (pendingChangeKey) {
            case 'gifterName':
            case 'sourceOfFunds':
            case 'sourceType':
            case 'otherSourceOfGrantDescription':
            case 'relationship':
            case 'cashOrMarketValue':
            case 'closingCosts':
            case 'isDeposited':
            case 'giftFundsProvisionPeriod':
            case 'financialInstitution':
            case 'accountNumber':
            case 'downPayment':
            case 'isUsedForDownPayment':
            case 'isUsedForIncome':
            case 'includeInAssets':

            case 'grantSourceName': {
              const conditionIfAssetTypeIsChanged = {
                gifterName: isAssetTypeGift || isGiftOfPropertyEquity,
                isDeposited:
                  (isAssetTypeGift || isGiftOfPropertyEquity) &&
                  checkBooleanPendingChangeVisibility(pendingChangeValue),
                giftFundsProvisionPeriod: isAssetTypeGift || isGiftOfPropertyEquity,
                cashOrMarketValue:
                  isMainAssetType || isAssetTypeGift || isGiftOfPropertyEquity || isOtherAssetType,
                sourceOfFunds: isGiftOfPropertyEquity,
                relationship: isAssetTypeGift,
                closingCosts: isAssetTypeGift || isGrantType,
                financialInstitution: isMainAssetType,
                accountNumber: isMainAssetType,
                downPayment: isAssetTypeGift || isGrantType,
                isUsedForDownPayment:
                  !isGrantType &&
                  !isAssetTypeGift &&
                  checkBooleanPendingChangeVisibility(pendingChangeValue),
                isUsedForIncome: checkBooleanPendingChangeVisibility(pendingChangeValue),
                sourceType: isGrantType && isAssetTypeSourceOfGrantEnabled,
                otherSourceOfGrantDescription:
                  isGrantType && isAssetTypeSourceOfGrantEnabled && isSourceTypeOther,
                includeInAssets:
                  (isGrantType || isAssetTypeGift) &&
                  checkBooleanPendingChangeVisibility(pendingChangeValue),
                grantSourceName: isGrantType,
              }[pendingChangeKey];

              return !isAssetTypeChanged || conditionIfAssetTypeIsChanged;
            }

            default:
              return true;
          }
        };

        const condition = getCondition();

        if (condition) {
          filteredPendingChangesAcc[pendingChangeKey] = pendingChangeValue;
        }

        return filteredPendingChangesAcc;
      },
      {},
    );

    acc[recordId] = filteredPendingChanges;

    return acc;
  }, {});

export const normalizeAssetsPendingChanges = ({
  initialPendingChanges,
  formPendingChanges = {},
  isAssetTypeSourceOfGrantEnabled,
}) => {
  const mergedPendingChanges = mergePendingChanges(initialPendingChanges, formPendingChanges);

  const filteredPendingChanges = filterPendingChanges({
    pendingChanges: mergedPendingChanges,
    isAssetTypeSourceOfGrantEnabled,
  });

  return filteredPendingChanges;
};
